import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Endpoint } from '@services/endpoints';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { StorageService, StorageType } from '../storage';

import { AuthService } from '@services/auth/';

import {
	IUser,
	IEquipment,
	IEquipmentTransfer,
	IRegistrationsServiceData,
	IRegistrationsSearchQuery,
	RegistrationsStorageKeys,
	TransferStatus,
	IRegistration,
	IRegistrationInfo
} from './registrations.models';

@Injectable({
	providedIn: 'root'
})
export class RegistrationsService {

	// main-switch to always fetch data from server (testing)
	private nocache = true;

	public data: IRegistrationsServiceData = {
		users: [],
		registrations: [],
		registrationInfo: [],
		bikesById: [],
		bikesBySerial: [],
		transferHistoryForBike: [],
		transfers: {
			open: {
				all: [],
				my: [],
				sourceUser: {},
				destinationEmail: {}
			},
			history: []
		}
	};

	private searchQuery: IRegistrationsSearchQuery = {
		query: {},
		ranges: {},
		pageSize: 25,
		pageNumber: 0,
		sortColumn: 'createdAt',
		sortOrder: 'desc' // TODO: Change to Status(?) when ready?
	};

	public set regSearchQuery(query: IRegistrationsSearchQuery) {
		this.searchQuery = { ...query };
		this.storageService.setItem(StorageType.sessionStorage, RegistrationsStorageKeys.registrationsSearchQuery, this.searchQuery);
	}

	public get regSearchQuery(): IRegistrationsSearchQuery {
		const query = this.storageService.getItem(StorageType.sessionStorage, RegistrationsStorageKeys.registrationsSearchQuery);

		if (query) {
			this.searchQuery = { ...this.searchQuery, ...query };

			for (const [key, value] of Object.entries(this.searchQuery.ranges)) {
				if (value[0]) {
					this.searchQuery.ranges[key][0] = parseInt(value[0] as any as string, 10);
				}
				if (value[1]) {
					this.searchQuery.ranges[key][1] = parseInt(value[1] as any as string, 10);
				}
			}
		}

		return this.searchQuery;
	}

	constructor(
		private http: HttpClient,
		private storageService: StorageService,
		private authService: AuthService,
		@Inject('BASE_URL') private baseUrl
	) {

	}

	public clearCache() {
		// TODO work out caching strategy
		// this.data.transfers = [];
	}

	public clearTransferHistory() {
		this.data.transferHistoryForBike = [];
		this.data.transfers = {
			open: {
				all: [],
				my: [],
				sourceUser: {},
				destinationEmail: {}
			},
			history: []
		};
	}

	public searchUsers(id: string, force = false): Observable<IUser[]> {
		const url = `${environment.registrations}${Endpoint.usersByEmail}/${id}`;
		console.log('searchUsers: ', url);
		this.data.users[id] = [{id: 'b8ae77aa38154093a10749ed387448bd', firstname: 'Admin', lastname: 'Admin', email: 'admin.bikeservice@specialized.com'}];

		if (force || !this.data.users[id]) {
			return this.http.get<IUser[]>(url).pipe(tap((res: IUser[]) => (this.data.users[id] = res)));
		} else {
			return of(this.data.users[id]);
		}
	}

	public bikesBySerialNumber(id: string, force = false): Observable<IEquipment[]> {
		const url = `${environment.registrations}${Endpoint.bikesBySerialNumber}?serial-number=${id}`;
		console.log('bikesBySerialNumber: ', url);

		if (this.nocache || force || !this.data.bikesBySerial[id]) {
			return this.http.get<IEquipment[]>(url).pipe(map((res: IEquipment[]) => (this.data.bikesBySerial[id] = res['specializedBikes'])));
		} else {
			return of(this.data.bikesBySerial[id]);
		}
	}

	public bikesById(id: string, force = false): Observable<IEquipment> {
		const url = `${environment.registrations}${Endpoint.bikesByIdentifier}/${id}`;
		console.log('bikesById: ', url);

		if (this.nocache || force || !this.data.bikesById[id]) {
			return this.http.get<IEquipment>(url).pipe(tap((res: IEquipment) => (this.data.bikesById[id] = res)));
		} else {
			return of(this.data.bikesById[id]);
		}
	}

	public getRegistrationInfo(bikeId: string, force = false): Observable<IRegistrationInfo> {
		const url = `${environment.registrations}${Endpoint.adminGetRegistrationInfo}?bikeId=` + bikeId;
		console.log('getRegistrationInfo: ', url);

		if (this.nocache || force || !this.data.registrationInfo[bikeId]) {
			return this.http.get<IRegistrationInfo>(url)
				.pipe(tap((res: IRegistrationInfo) => (this.data.registrationInfo[bikeId] = res)));
		} else {
			return of(this.data.registrationInfo[bikeId]);
		}
	}

	public getRegistrations(force = false): Observable<IRegistration[]> {
		const url = `${environment.registrations}${Endpoint.bikeRegistrations}`;
		console.log('getRegistrations: ', url);

		if (this.nocache || force || this.data.registrations.length === 0) {
			return this.http.get<IRegistration[]>(url)
				.pipe(tap((res: IRegistration[]) => (this.data.registrations = res)));
		} else {
			return of(this.data.registrations);
		}
	}

	public registerBike(id: string): Observable<IEquipment> {
		const params = { fromMissionControl: false, id: id, proofOfPurchaseId: "6ddc9e39-8930-43d1-a832-c8b582ce9d89" };
		const url = `${environment.registrations}${Endpoint.bikeRegistrationsSpecialized}`;
		console.log('registerBike: ', url);

		return new Observable<IEquipment>((observer) => {
			this.http.post<IEquipment>(url, params).subscribe(
				(bike: IEquipment) => {
					this.clearTransferHistory();

					observer.next(bike);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	public deleteCurrentRegistration(id: string): Observable<IEquipment> {
		const url = `${environment.registrations}${Endpoint.bikeRegistrations}/${id}`;
		console.log('deleteCurrentRegistration: ', url);

		return new Observable<IEquipment>((observer) => {
			this.http.delete<any>(url).subscribe(
				(res) => {
					observer.next(res);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			)
		});
	}

	public transferBike(id: string, email: string): Observable<IEquipment> {
		const params = { bikeId: id, email: email, adminUserIdentifier: this.authService.accountUser['id'] };
		const url = `${environment.registrations}${Endpoint.transferRequests}`;
		console.log('transferBike: ', url);

		return new Observable<IEquipment>((observer) => {
			this.http.post<IEquipment>(url, params).subscribe(
				(bike: IEquipment) => {
					observer.next(bike);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	public fetchTransferHistoryForBike(id: string): Observable<any> {
		const url = `${environment.registrations}${Endpoint.transferRequestsForBikeId}/${id}`;
		console.log('fetchTransferHistoryForBike: ', url);

		return new Observable<any[]>((observer) => {
			this.http.get<any[]>(url).subscribe(
				(res: any[]) => {
					observer.next(this.prepRequests(res));
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			)
		});
	}

	public getAllOpenTransfers(force = false): Observable<IEquipmentTransfer[]> {
		const url = `${environment.registrations}${Endpoint.transfersRequestsOpen}`;
		console.log('getAllOpenTransfers: ', url);

		if (this.nocache || force || this.data.transfers.open.all.length === 0) {
			return this.http.get<IEquipmentTransfer[]>(url)
				.pipe(tap((res: IEquipmentTransfer[]) => (this.data.transfers.open.all = this.prepRequests(res))));
		} else {
			return of(this.data.transfers.open.all);
		}
	}

	public getMyOpenTransfers(force = false): Observable<IEquipmentTransfer[]> {
		const url = `${environment.registrations}${Endpoint.transfersRequestsOpen}`;
		console.log('getMyOpenTransfers: ', url);

		if (this.nocache || force || !this.data.transfers.open.my.length) {
			return this.http.get<IEquipmentTransfer[]>(url).pipe(tap((res: IEquipmentTransfer[]) => (this.data.transfers.open.my = this.filterMyOpenTransfers(res))));
		} else {
			return of(this.data.transfers.open.my);
		}
	}

	public filterMyOpenTransfers(res: IEquipmentTransfer[]): IEquipmentTransfer[] {
		let filtered = [];
		const userId = this.authService.accountUser['id'];

		for (let resource of res) {
			if (resource['adminUserIdentifier'] === userId) {
				filtered.push(resource);
			}
		}

		return this.prepRequests(filtered);
	}

	public getSourceUsersOpenTransfers(force = false): Observable<IEquipmentTransfer[]> {
		const userId = this.authService.accountUser['id'];
		const url = `${environment.registrations}${Endpoint.transfersForSourceUser}/${userId}`;
		console.log('getSourceUsersOpenTransfers: ', url);

		if (this.nocache || force || !this.data.transfers.open.sourceUser[userId]) {
			return this.http.get<IEquipmentTransfer[]>(url).pipe(tap((res: IEquipmentTransfer[]) => (this.data.transfers.open.sourceUser[userId] = this.prepRequests(res))));
		} else {
			return of(this.data.transfers.open.sourceUser[userId]);
		}
	}

	public sentTransfers(id: string, force = false): Observable<IEquipmentTransfer[]> {
		const url = `${environment.registrations}${Endpoint.transfersForSourceUser}/${id}`;
		console.log('sentTransfers: ', url);

		if (this.nocache || force || !this.data.transfers.open.sourceUser[id]) {
			return this.http.get<IEquipmentTransfer[]>(url).pipe(tap((res: IEquipmentTransfer[]) => (this.data.transfers.open.sourceUser[id] = this.prepRequests(res))));
		} else {
			return of(this.data.transfers.open.sourceUser[id]);
		}
	}

	public receivedTransfers(id: string, force = false): Observable<IEquipmentTransfer[]> {
		const url = `${environment.registrations}${Endpoint.transfersForDestinationEmail}/${id}`;
		console.log('receivedTransfers: ', url);

		if (this.nocache || force || !this.data.transfers.open.destinationEmail[id]) {
			return this.http.get<IEquipmentTransfer[]>(url).pipe(map((res: IEquipmentTransfer[]) => (this.data.transfers.open.destinationEmail[id] = this.prepRequests(res))));
		} else {
			return of(this.data.transfers.open.destinationEmail[id]);
		}
	}

	public getAllTransfers(
		from: string,
		to: string,
		adminUserId?: string,
		status?: TransferStatus,
		force = false
	): Observable<IEquipmentTransfer[]> {
		const searchString = `from=${from}&to=${to}${
			adminUserId ? `&adminUserId=${adminUserId}` : ''
		}${status != TransferStatus.all ? `&status=${status}` : ''}`;
		const url = `${environment.registrations}${Endpoint.transfersForTimePeriod}?` + searchString;
		console.log('getAllTransfers: ', url);

		if (this.nocache || force || !this.data.transfers.history[searchString]) {
			return this.http.get<IEquipmentTransfer[]>(url).pipe(tap((res: IEquipmentTransfer[]) => (this.data.transfers.history[searchString] = this.prepRequests(res))));
		} else {
			return of(this.data.transfers.history[searchString]);
		}
	}

	private prepRequests(res: IEquipmentTransfer[]): IEquipmentTransfer[] {
		for (let i=0; i<res.length; i++) {
			let rec = res[i];

			if(rec['denied']) {
				rec.status = TransferStatus.denied;
			} else if (rec['accepted']) {
				rec.status = TransferStatus.accepted;
			} else {
				rec.status = TransferStatus.open;
			}
		}

		return res;
	}
}
